import React from "react";
import { DateTime } from "luxon";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useDispatch, useSelector } from "../../redux/hooks";
import { selectMidwives, selectSites } from "../../redux/slices/account";
import { updateTeskit } from "../../redux/slices/testkit";
import { DialogCloseHandler } from "../../types/dialog";
import { AccountData, AccountRole, SiteData } from "../../types/account";
import { Testkit, TestkitStatusAPI } from "../../types/testkit";
import { removeUndefined } from "../../utils/global";
import { isoDateTimeToLuxonUndef } from "../../utils/dateTime";
import BaseFormDialog from "../../components/dialogs/BaseFormDialog";

type KitValues = {
  site?: SiteData,
  paperLot?: string,
  paperExpiry: DateTime | null,
  swabLot?: string,
  swabExpiry: DateTime | null,
  additionalPaperLot?: string,
  additionalPaperExpiry: DateTime | null,
  additionalSwabLot?: string,
  additionalSwabExpiry: DateTime | null,
}

export const validatePaired = (lot: string | undefined, expiry: DateTime | null) => !!lot !== !!expiry ? 'Lot# & Expiry must be defined at the same time' : undefined;

const DialogModifyKit = ({
  testkit,
  open,
  onClose,
}: {
  testkit?: Testkit,
  open: boolean,
  onClose?: DialogCloseHandler,
}) => {
  const dispatch = useDispatch();
  const midwives = useSelector(selectMidwives);
  const sites = useSelector(selectSites);

  const [midwife, setMidwife] = React.useState<AccountData | undefined>(undefined);
  const [canSwitchSite, setCanSwitchSite] = React.useState(false);
  const [siteHelperText, setSiteHelperText] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!!testkit) {
      setMidwife(midwives.find(midwife => midwife.username === testkit.midwife));
    }
    else {
      setMidwife(undefined);
    }
  }, [testkit, midwives]);

  React.useEffect(() => {
    setCanSwitchSite(!!testkit && testkit.status !== TestkitStatusAPI.NEW && testkit.status !== TestkitStatusAPI.STOCK);

    let canSwitch = false;
    let helperText: string | undefined = undefined;

    if (!testkit) {
      // Dialog shouldn't be open
    }
    else if (testkit.status === TestkitStatusAPI.NEW || testkit.status === TestkitStatusAPI.STOCK) {
      helperText = 'Cannot change site as kit hasn\'t been allocated yet';
    }
    else if (!!testkit.patient) {
      helperText = 'Cannot change site as kit has been issued to a user';
    }
    else if (midwife?.role === AccountRole.MEDIC) {
      helperText = 'Cannot change site as kit is assigned to single-site midwife';
    }
    else {
      canSwitch = true;
    }

    setCanSwitchSite(canSwitch);
    setSiteHelperText(helperText);
  }, [testkit, midwife]);

  const handleValidate = (values: KitValues) => removeUndefined({
    paperLot: validatePaired(values.paperLot, values.paperExpiry),
    swabLot:  validatePaired(values.swabLot, values.swabExpiry),
    additionalPaperLot: validatePaired(values.additionalPaperLot, values.additionalPaperExpiry),
    additionalSwabLot:  validatePaired(values.additionalSwabLot, values.additionalSwabExpiry),
  })

  const handleSubmit = (values: KitValues) => {
    if (!testkit) return;

    // console.log(values)

    dispatch(updateTeskit({
      testkit,
      siteId: values.site?.id,
      paperLot: values.paperLot,
      paperExpiry: values.paperExpiry?.toISODate() || undefined,
      swabLot: values.swabLot,
      swabExpiry: values.swabExpiry?.toISODate() || undefined,
      additionalPaperLot: values.additionalPaperLot,
      additionalPaperExpiry: values.additionalPaperExpiry?.toISODate() || undefined,
      additionalSwabLot: values.additionalSwabLot,
      additionalSwabExpiry: values.additionalSwabExpiry?.toISODate() || undefined,
    }))
    .then(() => { onClose?.({}, 'closeClick') })
    .catch(() => {})
  }

  return (
    <BaseFormDialog
      title={`Edit testkit ${testkit?.id || '???'} details`}
      width={472}
      open={open}
      onClose={onClose}
      initialValues={{
        site: !!testkit ? sites.find(site => site.id === testkit.site) : undefined,
        paperLot: testkit?.paperLot,
        paperExpiry: isoDateTimeToLuxonUndef(testkit?.paperExpiry) || null,
        swabLot: testkit?.swabLot,
        swabExpiry: isoDateTimeToLuxonUndef(testkit?.swabExpiry) || null,
        additionalPaperLot: testkit?.additionalPaperLot,
        additionalPaperExpiry: isoDateTimeToLuxonUndef(testkit?.additionalPaperExpiry) || null,
        additionalSwabLot: testkit?.additionalSwabLot,
        additionalSwabExpiry: isoDateTimeToLuxonUndef(testkit?.additionalSwabExpiry) || null,
      } as KitValues}
      validate={handleValidate}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        // handleChange,
        // handleBlur,
        setFieldValue,
      }) => (
        <>
          <Autocomplete
            disabled={!canSwitchSite}
            disablePortal
            value={values.site}
            options={sites}
            getOptionKey={site => site.id}
            getOptionLabel={site => site.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={params => <TextField {...params} label='Site' helperText={siteHelperText}/>}
            onChange={(_, val) => setFieldValue('site', val)}
          />

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 4, mt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='h6'>Test Paper</Typography>

              <TextField
                label='Main Lot#'
                value={values.paperLot || ''}
                error={!!errors.paperLot}
                onChange={(e) => { setFieldValue('paperLot', e.target.value) }}
                sx={{ mt: 1 }}
              />
              <DatePicker
                label='Main Expiry'
                value={values.paperExpiry || null}
                onChange={val => setFieldValue('paperExpiry', val)}
                slotProps={{ textField: { error: !!errors.paperLot, helperText: errors.paperLot }}}
              />

              <TextField
                label='Extra Lot#'
                value={values.additionalPaperLot || ''}
                error={!!errors.additionalPaperLot}
                onChange={(e) => { setFieldValue('additionalPaperLot', e.target.value) }}
                sx={{ mt: 2 }}
              />
              <DatePicker
                label='Extra Expiry'
                value={values.additionalPaperExpiry || null}
                onChange={val => setFieldValue('additionalPaperExpiry', val)}
                slotProps={{ textField: { error: !!errors.additionalPaperLot, helperText: errors.additionalPaperLot }}}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='h6'>Swab</Typography>

              <TextField
                label='Main Lot#'
                value={values.swabLot || ''}
                error={!!errors.swabLot}
                onChange={(e) => { setFieldValue('swabLot', e.target.value) }}
                sx={{ mt: 1 }}
              />
              <DatePicker
                label='Main Expiry'
                value={values.swabExpiry || null}
                onChange={val => setFieldValue('swabExpiry', val)}
                slotProps={{ textField: { error: !!errors.swabLot, helperText: errors.swabLot }}}
              />

              <TextField
                label='Extra Lot#'
                value={values.additionalSwabLot || ''}
                error={!!errors.additionalSwabLot}
                onChange={(e) => { setFieldValue('additionalSwabLot', e.target.value) }}
                sx={{ mt: 2 }}
              />
              <DatePicker
                label='Extra Expiry'
                value={values.additionalSwabExpiry || null}
                onChange={val => setFieldValue('additionalSwabExpiry', val)}
                slotProps={{ textField: { error: !!errors.additionalSwabLot, helperText: errors.additionalSwabLot }}}
              />
            </Box>
          </Box>
        </>
      )}
    </BaseFormDialog>
  );
}

export default DialogModifyKit;